import type { CheckDescriptor } from '../../api/types'

/**
 * Panel order.
 *
 * The order itself comes from the API — each check module declares it — so
 * this is only the comparator. Everything else that used to live here (the
 * control for each parameter, the panel labels, the subtitles) moved to the
 * check modules, where adding a check is genuinely one file.
 */
export const byPanelOrder = (a: CheckDescriptor, b: CheckDescriptor) =>
  (a.ui?.order ?? Number.MAX_SAFE_INTEGER) - (b.ui?.order ?? Number.MAX_SAFE_INTEGER) ||
  a.key.localeCompare(b.key)

/**
 * A rough wall-clock estimate for the footer, so nobody starts a five-minute
 * run expecting it to be instant.
 *
 * Deliberately crude and deliberately client-side: it is a hint about what is
 * about to happen, not a figure the API should be asked to commit to. The
 * pairwise checks dominate, because they compare every plot against its
 * neighbours rather than against a reference layer.
 */
const PAIRWISE = new Set(['duplicates', 'partial_overlap'])

export function estimateRun(enabledKeys: string[], featureCount: number): string {
  const perPlot = enabledKeys.reduce(
    (ms, key) => ms + (PAIRWISE.has(key) ? 0.9 : 0.25),
    0,
  )
  const seconds = Math.max(1, Math.round((featureCount * perPlot) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.round(seconds / 60)
  return minutes < 60 ? `${minutes} min` : `${Math.round(minutes / 60)} h`
}
